<script lang="ts">
	// D14: Bosla has suspended the owner's restaurant (UC-23). It replaces
	// every section. The contact is a link here, a new mail and a WhatsApp
	// chat, because the owner is on a laptop or a phone, not a kiosk. The
	// reason never shows; only Bosla's admins see it.

	import BrandMark from '../components/BrandMark.svelte';
	import Button from '../components/Button.svelte';
	import { message, type Language } from '../i18n/messages';
	import type { Suspension } from '../suspension.svelte';
	import { whatsappHref, whatsappLabel } from '../contact';

	interface Props {
		language: Language;
		/** The restaurant's name, when the dashboard had loaded it. */
		restaurant: string;
		contact: Suspension;
		checking: boolean;
		onCheck: () => void;
		onSignOut: () => void;
	}

	let { language, restaurant, contact, checking, onCheck, onSignOut }: Props = $props();

	const label = $derived(
		whatsappLabel(message(language, 'suspendedWhatsapp'), contact.supportWhatsapp),
	);
</script>

<section class="screen" data-testid="owner-suspended">
	<div class="panel">
		<div class="brand">
			<BrandMark size={26} />
			{#if restaurant}<span class="name">{restaurant}</span>{/if}
		</div>
		<div class="head">
			<h1>{message(language, 'suspendedOwnerTitle')}</h1>
			<p>{message(language, 'suspendedOwnerTills')}</p>
		</div>
		<div class="contact">
			<span class="label">{message(language, 'suspendedOwnerContact')}</span>
			<Button
				testId="owner-suspended-email"
				label={contact.supportEmail}
				href="mailto:{contact.supportEmail}"
				tone="text"
				size="md"
				mono
			/>
			{#if contact.supportWhatsapp}
				<Button
					testId="owner-suspended-whatsapp"
					{label}
					href={whatsappHref(contact.supportWhatsapp)}
					newTab
					tone="text"
					size="md"
				/>
			{/if}
		</div>
		<div class="actions">
			<Button
				testId="owner-suspended-check"
				label={message(language, 'suspendedCheck')}
				tone="secondary"
				size="md"
				state={checking ? 'pending' : 'idle'}
				pendingLabel={message(language, 'suspendedChecking')}
				onPress={onCheck}
			/>
			<Button
				testId="owner-suspended-sign-out"
				label={message(language, 'ownerSignOut')}
				tone="text-quiet"
				size="sm"
				onPress={onSignOut}
			/>
		</div>
	</div>
</section>

<style>
	.screen {
		min-block-size: 100dvh;
		display: grid;
		place-items: center;
		padding: var(--space-9);
		background: var(--surface-page);
		color: var(--text-strong);
	}

	.panel {
		inline-size: 100%;
		max-inline-size: 480px;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
		padding: var(--card-pad);
	}

	.brand {
		display: flex;
		align-items: center;
		gap: var(--space-4);
	}

	.name {
		font-size: var(--text-md);
		font-weight: 700;
	}

	.head {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
		text-wrap: pretty;
	}

	p {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.contact {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-1);
		padding-block-start: var(--space-5);
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.label {
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.actions {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-4);
	}
</style>
